import React, { useEffect, useState } from "react";
import { Wallet, TrendingUp, Calendar, ArrowUpRight, ArrowDownLeft, AlertCircle, Lock, Percent } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { formatINR, formatDate } from "../../lib/format";
import { toast } from "sonner";

// Deposit interest earned across every deposit-protected rental this landlord
// is party to — 90% of the 8% p.a. interest on held deposits, locked until
// each 6-month cycle (from the deposit-paid date) unlocks.
function DepositInterestSection() {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const { data: rentals } = await api.get("/rentals");
        const protectedRentals = (rentals || []).filter((r) => r.deposit_protection);
        const results = await Promise.all(
          protectedRentals.map(async (r) => {
            try {
              const { data } = await api.get(`/landlord/interest/${r.id}`);
              return { rental: r, interest: data };
            } catch {
              return null;
            }
          })
        );
        setRows(results.filter((x) => x && x.interest.current_cycle));
      } catch (e) {
        console.error("Failed to load deposit interest:", e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return null;
  if (rows.length === 0) return null;

  const totalLocked = rows.reduce((sum, r) => sum + (r.interest.current_cycle?.my_locked_paise || 0), 0);

  return (
    <div className="card-base p-6 space-y-4">
      <div className="flex items-center gap-2">
        <Percent className="h-4 w-4 text-[#F97316]" />
        <h3 className="font-semibold">Deposit Interest</h3>
      </div>
      <p className="text-sm text-gray-500">
        90% of the 8% p.a. interest earned on deposits Orello holds for you — accrues monthly, unlocks to your wallet every 6 months from the date each deposit was paid.
      </p>
      {totalLocked > 0 && (
        <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-sm text-amber-800">
          <Lock className="h-4 w-4 shrink-0" />
          <span><span className="font-semibold">{formatINR(totalLocked)}</span> locked this cycle across your rentals</span>
        </div>
      )}
      <div className="space-y-2">
        {rows.map(({ rental, interest }) => {
          const cur = interest.current_cycle;
          return (
            <div key={rental.id} className="flex items-center justify-between p-3 border border-gray-100 rounded-lg text-sm">
              <div>
                <div className="font-medium text-gray-800">{rental.property_address}</div>
                <div className="text-xs text-gray-500">
                  {interest.days_remaining_in_cycle != null
                    ? `Unlocks in ${interest.days_remaining_in_cycle} day${interest.days_remaining_in_cycle === 1 ? "" : "s"}`
                    : "—"}
                </div>
              </div>
              <div className="font-semibold text-amber-700">{formatINR(cur?.my_locked_paise || 0)}</div>
            </div>
          );
        })}
      </div>
      <p className="text-[11px] text-gray-400">
        Any applicable platform fee or GST will be deducted at the time of withdrawal, if applicable.
      </p>
    </div>
  );
}

function WalletStat({ icon: Icon, label, value, color }) {
  const colors = {
    emerald: "bg-emerald-50 text-emerald-600",
    indigo: "bg-[#FFF4ED] text-[#F97316]",
    amber: "bg-amber-50 text-amber-600",
  };
  return (
    <div className="card-base p-6">
      <div className="flex items-center gap-3">
        <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${colors[color]}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div className="text-sm text-gray-500">{label}</div>
      </div>
      <div className="mt-3 text-3xl font-bold font-display">{value}</div>
    </div>
  );
}

function WithdrawalModal({ open, onClose, balance, onSuccess }) {
  const [form, setForm] = useState({
    amount: "",
    account_holder_name: "",
    account_number: "",
    ifsc_code: "",
    account_type: "savings",
  });
  const [busy, setBusy] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.amount || !form.account_number || !form.ifsc_code) {
      toast.error("All fields are required");
      return;
    }
    const amount = parseFloat(form.amount);
    if (amount <= 0 || amount > balance / 100) {
      toast.error(`Enter amount between ₹1 and ₹${(balance / 100).toFixed(2)}`);
      return;
    }
    setBusy(true);
    try {
      await api.post("/wallet/withdraw", {
        amount: amount,
        account_holder_name: form.account_holder_name,
        account_number: form.account_number,
        ifsc_code: form.ifsc_code.toUpperCase(),
        account_type: form.account_type,
      });
      toast.success(`Withdrawal of ₹${amount.toFixed(2)} initiated! Expect it in 3-5 business days.`);
      setForm({ amount: "", account_holder_name: "", account_number: "", ifsc_code: "", account_type: "savings" });
      onClose();
      onSuccess();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-md w-full p-6 space-y-4">
        <h2 className="text-lg font-semibold">Withdraw Funds</h2>
        <p className="text-sm text-gray-500">Available balance: {formatINR(balance)}</p>

        <form onSubmit={handleSubmit} className="space-y-3">
          {/* Amount */}
          <div>
            <label className="text-xs font-medium text-gray-700 block mb-1">Amount *</label>
            <div className="relative">
              <span className="absolute left-3 top-2.5 text-gray-500 font-medium">₹</span>
              <input
                type="number"
                placeholder="0.00"
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                className="w-full border border-gray-200 rounded-lg pl-8 pr-3 py-2 text-sm"
                step="0.01"
              />
            </div>
          </div>

          {/* Account Holder Name */}
          <div>
            <label className="text-xs font-medium text-gray-700 block mb-1">Account Holder Name *</label>
            <input
              type="text"
              placeholder="Your name"
              value={form.account_holder_name}
              onChange={(e) => setForm({ ...form, account_holder_name: e.target.value })}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
            />
          </div>

          {/* Account Number */}
          <div>
            <label className="text-xs font-medium text-gray-700 block mb-1">Account Number *</label>
            <input
              type="text"
              placeholder="e.g., 12345678901234"
              value={form.account_number}
              onChange={(e) => setForm({ ...form, account_number: e.target.value })}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
            />
          </div>

          {/* IFSC Code */}
          <div>
            <label className="text-xs font-medium text-gray-700 block mb-1">IFSC Code *</label>
            <input
              type="text"
              placeholder="e.g., HDFC0000001"
              value={form.ifsc_code}
              onChange={(e) => setForm({ ...form, ifsc_code: e.target.value })}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
            />
          </div>

          {/* Account Type */}
          <div>
            <label className="text-xs font-medium text-gray-700 block mb-1">Account Type</label>
            <select
              value={form.account_type}
              onChange={(e) => setForm({ ...form, account_type: e.target.value })}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
            >
              <option value="savings">Savings</option>
              <option value="current">Current</option>
            </select>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-700">
            <p className="font-medium mb-1">Processing Time</p>
            <p>Withdrawals are processed within 3-5 business days.</p>
          </div>

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              className="flex-1 border border-gray-300 text-gray-700 rounded-lg py-2 text-sm font-medium hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={busy}
              className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg py-2 text-sm font-medium disabled:opacity-50"
            >
              {busy ? "Processing..." : "Withdraw"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function LandlordWallet() {
  const [balance, setBalance] = useState(0);
  const [totalEarned, setTotalEarned] = useState(0);
  const [totalWithdrawn, setTotalWithdrawn] = useState(0);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);

  const loadWalletData = async () => {
    setLoading(true);
    try {
      const [walletRes, txnRes] = await Promise.all([
        api.get("/wallet"),
        api.get("/wallet/transactions"),
      ]);
      setBalance(Math.round(walletRes.data.balance * 100));
      setTotalEarned(Math.round(walletRes.data.total_earned * 100));
      setTotalWithdrawn(Math.round(walletRes.data.total_withdrawn * 100));
      setTransactions(txnRes.data.transactions || []);
    } catch (e) {
      console.error("Failed to load wallet:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadWalletData();
  }, []);

  return (
    <div className="space-y-6" data-testid="landlord-wallet">
      <div>
        <h1 className="text-3xl font-bold font-display">Orello Wallet</h1>
        <p className="text-sm text-gray-500 mt-1">Manage your earnings and deductions.</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <WalletStat icon={Wallet} label="Available Balance" value={formatINR(balance)} color="emerald" />
        <WalletStat icon={TrendingUp} label="Total Earned" value={formatINR(totalEarned)} color="indigo" />
        <WalletStat icon={Calendar} label="Total Withdrawn" value={formatINR(totalWithdrawn)} color="amber" />
      </div>

      {/* Withdrawal CTA */}
      {balance > 0 && (
        <div className="bg-gradient-to-r from-emerald-50 to-emerald-100 border border-emerald-200 rounded-xl p-6 flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-emerald-900">Ready to withdraw?</h3>
            <p className="text-sm text-emerald-700 mt-1">You have {formatINR(balance)} available to withdraw anytime.</p>
          </div>
          <button
            onClick={() => setShowWithdrawModal(true)}
            className="bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-2 rounded-lg font-medium whitespace-nowrap"
          >
            Withdraw Funds
          </button>
        </div>
      )}

      <DepositInterestSection />

      {/* Transactions */}
      <div className="card-base p-6">
        <h3 className="font-semibold mb-4">Transaction History</h3>
        {loading ? (
          <div className="text-center py-8 text-gray-500">Loading...</div>
        ) : transactions.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <p>No transactions yet.</p>
            <p className="text-sm mt-1">Your earnings and withdrawals will appear here.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {transactions.map((txn) => (
              <div key={txn.id} className="flex items-center justify-between p-4 border border-gray-100 rounded-lg hover:bg-gray-50 transition">
                <div className="flex items-center gap-3">
                  <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${
                    txn.type.includes("credit") ? "bg-emerald-100 text-emerald-600" : "bg-amber-100 text-amber-600"
                  }`}>
                    {txn.type.includes("credit") ? <ArrowDownLeft className="h-5 w-5" /> : <ArrowUpRight className="h-5 w-5" />}
                  </div>
                  <div>
                    <div className="font-medium text-sm text-gray-800 capitalize">
                      {txn.type === "commission_credit" && "Commission Earned"}
                      {txn.type === "deduction_credit" && "Deduction Credited"}
                      {txn.type === "deposit_interest" && "Deposit Interest"}
                      {txn.type === "withdrawal_debit" && "Withdrawal"}
                      {txn.type === "other" && "Other"}
                    </div>
                    <div className="text-xs text-gray-500">
                      {txn.description || formatDate(txn.created_at)}
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div className={`font-semibold ${txn.type.includes("credit") ? "text-emerald-600" : "text-amber-600"}`}>
                    {txn.type.includes("credit") ? "+" : "-"}{formatINR(txn.amount_paise)}
                  </div>
                  <div className="text-xs text-gray-500 capitalize">
                    {txn.status === "processing" && "Processing..."}
                    {txn.status === "completed" && "Completed"}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Withdrawal Modal */}
      <WithdrawalModal
        open={showWithdrawModal}
        onClose={() => setShowWithdrawModal(false)}
        balance={balance}
        onSuccess={loadWalletData}
      />
    </div>
  );
}
